// TabContent1.js
import React, { useState, useEffect, useRef } from 'react';
import ProgressionTab from '../../../../theme/ProgressionBuilder/ProgressionTab';
import { useTheme } from '@emotion/react';
import Text from '../../../../theme/text/Text';
import { useTranslation } from 'react-i18next';
import { Box, Checkbox, InputBase } from '@mui/material';
import { tokens } from '../../../../../theme';
import NumberInput from '../../../../theme/inputs/NumberInput';
import ShDateRangePicker from "../../../../theme/ShDateRangePicker";
import ShValidatedInput from '../../../../theme/inputs/ShValidatedInput';
import { ENV_TYPES } from "../../../Sidebar";
import { useDispatch, useSelector } from 'react-redux';
import * as yup from "yup";
import { yupRequired, yupNumberRequired } from '../../../../../utils/validation';
import { setTabErrors } from '../../../../../newapi/global/progressionTabSlice';
import { useGetRelationsSelectQuery } from '../../../../../newapi/global/relation/relationSlice';
import { useGetWarehousesSelectQuery } from '../../../../../newapi/warehouse/warehouseApiSlice';
import { useGetSuppliersSelectQuery } from '../../../../../newapi/global/supplier/supplierSlice';
import ShValidatedInputDropdown from '../../../../theme/dropdowns/ShValidatedInputDropdown';
import ShSwitch from '../../../../theme/inputs/ShSwitch';
import EMGeneralExplainerBox from '../../../ModalsPopover/explainers/EMGeneralExplainerBox';
import { useGetSettingsForRelationQuery, useGetSettingsQuery } from '../../../../../newapi/global/generalApiSlice';

/**
 * TabContent1 Component
 * 
 * Props:
 * - handleDataUpdate: Function, updates the data in the Redux state.
 * - handleNext: Function, navigates to the next tab.
 */
const Content = (props) => {
    const theme = useTheme();
    const {t} = useTranslation();
    const colors = tokens(theme.palette.mode);
    const data = { ...props.data };
    const [loaded, setLoaded] = useState(false);
    const dispatch = useDispatch();
    const { data: relations, isLoading } = useGetRelationsSelectQuery();
   
    const { data: warehouseData, isLoading: isWarehousesLoading } = useGetWarehousesSelectQuery();
    const selectedActiveWarehouseId = useSelector(state => state.warehouse.activeId);

    // Get values to determine if the relation dropdown should be shown
    const isFulfilment = useSelector((state) => state.settings.environment.isFulfilment);
    const selectedEnvironmentType = useSelector((state) => state.settings.environment.selectedEnvironmentType);
    const isRelation = selectedEnvironmentType === ENV_TYPES[200];

    // Relations may only add a remark when the instructions right is enabled
    const { data: relationSettings } = useGetSettingsForRelationQuery(undefined, { skip: !isRelation });
    const allowInboundRemark = isRelation && relationSettings?.find(setting => setting.key === "relation_edit_product_instructions")?.value === "true";

    // Saving as a concept is only offered when the warehouse has it enabled
    const { data: conceptSettings } = useGetSettingsQuery("inbound_concept_enabled");
    const conceptEnabled = conceptSettings?.find(setting => setting.key === "inbound_concept_enabled")?.value === "true";

    // Supplier query - skip if not isRelation and no relationId selected
    const shouldFetchSuppliers = isRelation || data.relationId;
    const { data: suppliersData, isLoading: isSuppliersLoading } = useGetSuppliersSelectQuery(
        data.relationId ? data.relationId : null,
        { skip: !shouldFetchSuppliers }
    );

    const selectedSupplier = suppliersData?.find(item => item.id === data.supplierId);

    // From Inkoopadvies the mail is previewed, edited and confirmed in an extra last step instead of
    // the switch below; a supplier without an address skips it.
    const supplierMailStep = !!data.fromPurchaseAdvice && !!selectedSupplier?.email;
    const mailSupplierId = useRef(data.supplierId);
    useEffect(() => {
        // A mail edited for an earlier choice must never go out with this one. The step's switch
        // starts as the supplier's own choice whether every inbound is mailed.
        if (!!data.supplierMailStep !== supplierMailStep || mailSupplierId.current !== data.supplierId) {
            mailSupplierId.current = data.supplierId;
            props.handleDataUpdate({ supplierMailStep, supplierMail: null, sendToSupplier: supplierMailStep && !!selectedSupplier.autoSendInbound });
        }
    }, [supplierMailStep, data.supplierId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        // setWarehouses(warehouseData?.data ?? []);
        if(warehouseData?.data?.length === 1) {
            handleInput("warehouseId",warehouseData?.data[0].id);
        } else {
            if(selectedActiveWarehouseId !== -1){
                handleInput("warehouseId",selectedActiveWarehouseId)
            }

        }
    }, [warehouseData,selectedActiveWarehouseId]);


    useEffect(() => {
        if(!isFulfilment && !(selectedEnvironmentType === ENV_TYPES[200])) {
            handleInput("relationId",1);
        }
    },[isFulfilment,selectedEnvironmentType])

    const handleInput = (key, val) => {
        props.handleDataUpdate({ [key]: val });
        dispatch(setTabErrors({ ...props.errors, [key]: '' }));
    };

    const select = (date) => {
        const formattedDate = date[0].startDate.toISOString().split('T')[0];
        props.handleDataUpdate({ shipmentDate: formattedDate });
    }

    // Set virtual product is true for POST request
    useEffect(() => {
        if(loaded) {
            props.handleDataUpdate({ isVirtual: true });
        }
        setLoaded(true);
    }, [loaded]);

    const labelWidth = 220;

    return (
        <>
            <Box 
                sx={{
                    display:'flex',
                    flexDirection:"column",
                }}
            >
                {isFulfilment && !isRelation && (
                <Box 
                    sx={{
                        width:1,
                        display:"flex",
                        paddingTop: 1.5,
                        alignItems:"center",
                        justifyContent:"space-between",
                        gap:7,
                        marginBottom: 1
                    }}
                >
                    
                    <Box
                        sx={{
                            width:labelWidth,
                            display:"flex",
                            alignItems:"center",
                        }}
                    >
                        <Text>{t("relation")}</Text>
                    </Box>
                    <Box
                        sx={{
                            flex:1,
                            backgroundColor : `${theme.palette.mode === "dark" ?colors.grey[300] : colors.grey[100]}`,
                            borderRadius:4,
                            display:"flex",
                            height:32
                        }}
                    >
                        <ShValidatedInputDropdown
                            key={data.relationId}
                            displayName={"name"} 
                            changeField={"id"}
                            valueKey={"id"}
                            closeOnSelection
                            searchOptions={{
                                enabled: true,
                                placeholder: t("search_relation_placeholder")
                            }}
                            styles={{
                                minWidth:300
                            }}

                            disallowDeselection
                            name="relationId"
                            selected={relations?.find(item => item.id === data.relationId)?.id ?? -1}
                            options={relations ? relations : []}
                            noSelection={t("choose_option")} 
                            onChange={handleInput}
                            error={props.errors.relationId}
                        />
                    </Box>
                </Box>
                )}
                {warehouseData?.data && warehouseData?.data.length > 1 && (
                <Box 
                    sx={{
                        width:1,
                        display:"flex",
                        paddingTop: 1.5,
                        alignItems:"center",
                        justifyContent:"space-between",
                        gap:7,
                        marginBottom: 1
                    }}
                >
                    
                    <Box
                        sx={{
                            width:labelWidth,
                            display:"flex",
                            alignItems:"center",
                        }}
                    >
                        <Text>{t("warehouse")}</Text>
                    </Box>
                    <Box
                        sx={{
                            flex:1,
                            backgroundColor : `${theme.palette.mode === "dark" ?colors.grey[300] : colors.grey[100]}`,
                            borderRadius:4,
                            display:"flex",
                            height:32
                        }}
                    >
                        <ShValidatedInputDropdown
                            key={data.warehouseId}
                            displayName={"name"}
                            changeField={"id"}
                            valueKey={"id"}
                            closeOnSelection
                            name="warehouseId"
                            selected={warehouseData?.data?.find(item => item.id === data.warehouseId)?.id ?? -1}
                            options={warehouseData?.data ? warehouseData?.data : []}
                            noSelection={t("choose_option")}
                            onChange={handleInput}
                            error={props.errors.warehouseId}
                        />
                    </Box>
                </Box>
                )}
                {suppliersData && suppliersData.length > 0 && (
                <Box
                    sx={{
                        width:1,
                        display:"flex",
                        paddingTop: 1.5,
                        alignItems:"center",
                        justifyContent:"space-between",
                        gap:7,
                        marginBottom: 1
                    }}
                >

                    <Box
                        sx={{
                            width:labelWidth,
                            display:"flex",
                            alignItems:"center",
                        }}
                    >
                        <Text>{t("supplier")}</Text>
                    </Box>
                    <Box
                        sx={{
                            flex:1,
                            backgroundColor : `${theme.palette.mode === "dark" ?colors.grey[300] : colors.grey[100]}`,
                            borderRadius:4,
                            display:"flex",
                            height:32
                        }}
                    >
                        <ShValidatedInputDropdown
                            key={data.supplierId}
                            displayName={"name"}
                            changeField={"id"}
                            valueKey={"id"}
                            closeOnSelection
                            searchOptions={{
                                enabled: true,
                                placeholder: t("search_supplier_placeholder")
                            }}
                            styles={{
                                minWidth:300
                            }}
                            name="supplierId"
                            selected={suppliersData?.find(item => item.id === data.supplierId)?.id ?? -1}
                            // "None" clears the supplier, so the next step offers every product again.
                            options={[{ id: null, name: t("none") }, ...(suppliersData ?? [])]}
                            noSelection={t("choose_option")}
                            onChange={handleInput}
                            error={props.errors.supplierId}
                        />
                    </Box>
                </Box>
                )}
                {selectedSupplier && !supplierMailStep && (
                <Box
                    sx={{
                        width:1,
                        paddingTop: 1.5,
                        display:"flex",
                        alignItems:"center",
                        justifyContent:"space-between",
                        gap:7
                    }}
                >

                    <Box
                        sx={{
                            width:labelWidth,
                            display:"flex",
                            alignItems:"center",
                        }}
                    >
                        <Text>{t("send_inbound_to_supplier")}</Text>
                        <EMGeneralExplainerBox title={t("send_inbound_to_supplier")} text={t("send_inbound_to_supplier_explainer_text")}/>
                    </Box>

                    <Box
                        sx={{
                            flex:1,
                            borderRadius:4,
                            display:"flex",
                            alignItems:"center",
                            gap:1,
                            height:32
                        }}
                    >
                    {/* A supplier set to receive every inbound gets the mail regardless, so the switch
                        only shows that; without an email address there is nowhere to send it to. */}
                    <ShSwitch
                        checked={selectedSupplier.autoSendInbound ? !!selectedSupplier.email : (data.sendToSupplier ?? false)}
                        disabled={!selectedSupplier.email || !!selectedSupplier.autoSendInbound}
                        onChange={(e) => handleInput('sendToSupplier', e.target.checked)}
                    />
                    {!selectedSupplier.email && <Text light>{t("supplier_has_no_email")}</Text>}
                    </Box>
                </Box>
                )}
                <Box
                    sx={{
                        width:1,
                        paddingTop: 1.5,
                        display:"flex",
                        alignItems:"center",
                        justifyContent:"space-between",
                        gap:7
                    }}
                >
                    <Box
                        sx={{
                            width:labelWidth,
                        }}
                    >
                        <Text>{t("reference")}</Text>
                    </Box>
                    
                    <Box
                        sx={{
                            flex:1,
                            backgroundColor : `${theme.palette.mode === "dark" ?colors.grey[300] : colors.grey[100]}`,
                            borderRadius:4,
                            display:"flex",
                            height:32
                        }}
                    >
                        <ShValidatedInput
                            name="reference"
                            value={data.referenceIsPoNumber ? "" : data.reference}
                            disabled={!!data.referenceIsPoNumber}
                            onChange={handleInput}
                            error={props.errors.reference}
                        />
                    </Box>
                </Box>
                <Box
                    sx={{
                        width:1,
                        display:"flex",
                        alignItems:"center",
                        justifyContent:"space-between",
                        gap:7
                    }}
                >
                    <Box sx={{ width:labelWidth }}/>
                    {/* The PO number only exists once the inbound is saved, so the backend fills it in as the reference. */}
                    <Box sx={{ flex:1, display:"flex", alignItems:"center", gap:0.5 }}>
                        <Checkbox
                            checked={!!data.referenceIsPoNumber}
                            onChange={(e) => handleInput('referenceIsPoNumber', e.target.checked)}
                            size="small"
                            sx={{
                                color: colors.grey[300],
                                '&.Mui-checked': { color: colors.orange[400] },
                            }}
                        />
                        <Box onClick={() => handleInput('referenceIsPoNumber', !data.referenceIsPoNumber)} sx={{ cursor: "pointer" }}>
                            <Text>{t("reference_is_po_number")}</Text>
                        </Box>
                    </Box>
                </Box>
                <Box 
                    sx={{
                        width:1,
                        paddingTop: 1.5,
                        display:"flex",
                        alignItems:"center",
                        justifyContent:"space-between",
                        gap:7
                    }}
                >
                    
                    <Box
                        sx={{
                            width:labelWidth,
                            display:"flex",
                            alignItems:"center",
                        }}
                    >
                        <Text>{t("shipment_date")}</Text>
                    </Box>
                    
                    <Box
                        sx={{
                            flex:1,
                            backgroundColor : `${theme.palette.mode === "dark" ?colors.grey[300] : colors.grey[100]}`,
                            borderRadius:4,
                            display:"flex",
                            height:32
                        }}
                    >
                        <ShDateRangePicker 
                            singleSelect={true} 
                            // startDate={"2024-12-13"} 
                            select={select} 
                            size={32}
                        >

                        </ShDateRangePicker>
                    </Box>
                    
                </Box>
                <Box 
                    sx={{
                        width:1,
                        paddingTop: 1.5,
                        display:"flex",
                        alignItems:"center",
                        justifyContent:"space-between",
                        gap:7
                    }}
                >
                    
                    <Box
                        sx={{
                            width:labelWidth,
                        }}
                    >
                        <Text>{t("pallets")} ({t("_optional")})</Text>
                    </Box>
                    
                    <Box
                        sx={{
                            flex:1,
                            backgroundColor : `${theme.palette.mode === "dark" ?colors.grey[300] : colors.grey[100]}`,
                            borderRadius:4,
                            display:"flex",
                            height:32
                        }}
                    >
                        <NumberInput btnSize={"24"}
                                    value={data.pallets ?? 0}
                                change={(e) => handleInput('pallets', e)}/>
                    </Box>
                </Box>
                <Box 
                    sx={{
                        width:1,
                        paddingTop: 1.5,
                        display:"flex",
                        alignItems:"center",
                        justifyContent:"space-between",
                        gap:7
                    }}
                >
                    
                    <Box
                        sx={{
                            width:labelWidth,
                        }}
                    >
                        <Text>{t("colli")} ({t("_optional")})</Text>
                    </Box>
                    
                    <Box
                        sx={{
                            flex:1,
                            backgroundColor : `${theme.palette.mode === "dark" ?colors.grey[300] : colors.grey[100]}`,
                            borderRadius:4,
                            display:"flex",
                            height:32
                        }}
                    >
                        <NumberInput btnSize={"24"}
                                    value={data.colli ?? 0}
                                change={(e) => handleInput('colli', e)}/>
                    </Box>
                </Box>
                {allowInboundRemark && (
                <Box
                    sx={{
                        width:1,
                        paddingTop: 1.5,
                        display:"flex",
                        alignItems:"center",
                        justifyContent:"space-between",
                        gap:7
                    }}
                >

                    <Box
                        sx={{
                            width:labelWidth,
                        }}
                    >
                        <Text>{t("remark")}</Text>
                    </Box>

                    <Box
                        sx={{
                            flex:1,
                            backgroundColor : `${theme.palette.mode === "dark" ?colors.grey[300] : colors.grey[100]}`,
                            borderRadius:4,
                            display:"flex",
                            height:32
                        }}
                    >
                        <ShValidatedInput
                            name="relationRemark"
                            value={data.relationRemark}
                            onChange={handleInput}
                            error={props.errors.relationRemark}
                        />
                    </Box>
                </Box>
                )}
                <Box
                    sx={{
                        width:1,
                        paddingTop: 1.5,
                        display:"flex",
                        alignItems:"center",
                        justifyContent:"space-between",
                        gap:7
                    }}
                >

                    <Box
                        sx={{
                            width:labelWidth,
                            display:"flex",
                            alignItems:"center",
                        }}
                    >
                        <Text>{t("requires_customs_clearance")}</Text>
                        <EMGeneralExplainerBox title={t("requires_customs_clearance_explainer_title")} text={t("requires_customs_clearance_explainer_text")}/>
                    </Box>
                    
                    <Box
                        sx={{
                            flex:1,
                            borderRadius:4,
                            display:"flex",
                            height:32
                        }}
                    >
                    <ShSwitch
                        checked={data.requiresCustomsClearance ?? false}
                        onChange={(e) => handleInput('requiresCustomsClearance', e.target.checked)}
                    />
                    </Box>
                </Box>
                {conceptEnabled && (
                <Box
                    sx={{
                        width:1,
                        paddingTop: 1.5,
                        display:"flex",
                        alignItems:"center",
                        justifyContent:"space-between",
                        gap:7
                    }}
                >

                    <Box
                        sx={{
                            width:labelWidth,
                            display:"flex",
                            alignItems:"center",
                        }}
                    >
                        <Text>{t("save_as_concept")}</Text>
                        <EMGeneralExplainerBox title={t("save_as_concept_explainer_title")} text={t("save_as_concept_explainer_text")}/>
                    </Box>

                    <Box
                        sx={{
                            flex:1,
                            borderRadius:4,
                            display:"flex",
                            height:32
                        }}
                    >
                    <ShSwitch
                        checked={data.isConcept ?? false}
                        onChange={(e) => handleInput('isConcept', e.target.checked)}
                    />
                    </Box>
                </Box>
                )}
            </Box>
        </>
    );
};

const Details = (props) => {
    const {t} = useTranslation();
    // Inkoopadvies adds a mail preview step for suppliers with an email address (see Details).
    const supplierMailStep = useSelector((state) => !!state.progressiontabs.data?.supplierMailStep);

    const isFulfilment = useSelector((state) => state.settings.environment.isFulfilment);
    
    const schema = yup.object({
        reference: yupRequired('reference',t),
        shipmentDate: yupRequired('shipment_date',t),
    });

    // Create conditional schema
    const conditionalSchema = [
        {
            key: "reference",
            errorField: t('reference'),
            // With the PO number as reference the field stays empty
            condition: (data) => !data.referenceIsPoNumber,
            type: "text",
        },
        {
            key: "reference",
            errorField: t('shipment_date'),
            condition: (data) => !data.referenceIsPoNumber,
            type: "text",
        },
        {
            key: "relationId",
            errorField: t('relation'),
            alwaysRequired: isFulfilment, // Always required if isFulfilment is true
            condition: (data) => false, // If not alwaysRequired, return false
            type: "integer",
        },
    ];

    return (
        <ProgressionTab 
            tabId="tabDetails" 
            subtitle={t("Details")} 
            step={1}
            nextTab={"tabInboundLines"}
            maxSteps={supplierMailStep ? 4 : 3}
            conditionalSchema={conditionalSchema}
        >
            <Content/>
        </ProgressionTab>
    )
}

export default Details;

